import { createContext, useContext } from 'react';

import type { ApolloError } from '@apollo/client';

import type { GetUserExperienceContextQuery } from 'bundles/unified-common/queries/__generated__/GetUserExperienceContextQuery';

type GetUserExperienceContextSelection = NonNullable<
  NonNullable<GetUserExperienceContextQuery['UserExperienceContext']>['getUserExperienceContext']
>;

/**
 * Stored user experience selection from the API (consumer, degree, or enterprise).
 * Null when the user has no stored record or before the initial fetch completes.
 */
export type UserExperienceSelection = GetUserExperienceContextSelection | null;

/**
 * Selection type for updating the user's experience context.
 * Use with organizationId for enterprise, degreeProgramId for degree.
 */
export type UserExperienceSelectionType = 'consumer' | 'degree' | 'enterprise';

export type UpdateUserExperienceInput = {
  selection: UserExperienceSelectionType;
  organizationId?: string;
  degreeProgramId?: string;
};

export type UserExperienceContextValue = {
  /** Stored experience selection; null if not yet set or before fetch. */
  userExperience: UserExperienceSelection;
  isLoading: boolean;
  error: ApolloError | null;
  updateUserExperience: (input: UpdateUserExperienceInput) => Promise<UserExperienceSelection>;
  isUpdating: boolean;
};

const defaultContextValue: UserExperienceContextValue = {
  userExperience: null,
  isLoading: false,
  error: null,
  updateUserExperience: () => Promise.resolve(null),
  isUpdating: false,
};

export const UserExperienceContext = createContext<UserExperienceContextValue>(defaultContextValue);

/**
 * Full context value: userExperience, isLoading, error, updateUserExperience, isUpdating.
 */
export const useUserExperienceContext = (): UserExperienceContextValue => useContext(UserExperienceContext);

export type UseUpdateUserExperienceReturn = {
  update: UserExperienceContextValue['updateUserExperience'];
  isUpdating: boolean;
  error: ApolloError | null;
};

/**
 * Hook for updating user experience only. Returns { update, isUpdating, error }.
 */
export const useUpdateUserExperience = (): UseUpdateUserExperienceReturn => {
  const { updateUserExperience, isUpdating, error } = useUserExperienceContext();
  return { update: updateUserExperience, isUpdating, error };
};
